/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import {
    ContentScreenDetails,
    ContentScreenType,
    EpisodeScreenDetails,
    GameScreenDetails,
    MovieScreenDetails,
} from 'client-code/actions';

import { Locale, LocaleKeys } from '../../core/locale';
import { DateTime } from '../../core/utils';

export class ProgramScreenHelper {
    /**
     * This method dynamically constructs the title and title suffix based on the type of content
     * screen.
     *
     * @param {ContentScreenDetails} contentScreenDetails - The details of the content screen,
     * including the title and type.
     * @returns {Object} An object containing:
     *   - `title` (string): The base title of the content.
     *   - `titleSuffix` (string): The dynamically generated suffix based on the content type.
     */
    public static generateScreenTitle(contentScreenDetails: ContentScreenDetails): {
        title: string;
        titleSuffix: string;
    } {
        const { title, contentScreenType } = contentScreenDetails;
        let modifiedTitle = title;
        let titleSuffix = '';
        switch (contentScreenType) {
            case ContentScreenType.EpisodeScreenDetails: {
                const { episodeNumber, firstAiredDate, seasonNumber } =
                    contentScreenDetails as EpisodeScreenDetails;
                const episodeText: string =
                    episodeNumber && episodeNumber > 0 ? `E${episodeNumber}` : '';
                const seasonText: string =
                    seasonNumber && seasonNumber > 0 ? `S${seasonNumber} ` : '';
                const seasonEpisodeText = (seasonText + episodeText).length
                    ? `${seasonText}${episodeText} `
                    : '';
                if (seasonEpisodeText.length) {
                    titleSuffix = (seasonText + episodeText).length
                        ? `${seasonText}${episodeText} `
                        : '';
                } else if (firstAiredDate) {
                    titleSuffix = `${new DateTime(firstAiredDate).strftime('%M/%D/%y')}`;
                } else {
                    titleSuffix = `${Locale.getString(
                        LocaleKeys.EPISODE_SCREEN_TITLE_ULTIMATE_FALLBACK,
                    )}`;
                }
                break;
            }
            case ContentScreenType.MovieScreenDetails: {
                const { movieYear } = contentScreenDetails as MovieScreenDetails;
                if (movieYear) titleSuffix = `(${movieYear})`;
                break;
            }
            case ContentScreenType.GameScreenDetails: {
                const { gameTitle } = contentScreenDetails as GameScreenDetails;
                if (gameTitle) modifiedTitle += ` - ${gameTitle}`;
                break;
            }
            default:
        }

        return { title: modifiedTitle, titleSuffix };
    }
}
